import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { getArticlesByCategory, getFeaturedArticles, getCategories } from '../services/api';
import { CategoryIcons, getCategoryIcon, DefaultIcon } from '../components/CategoryIcons';
import { useDocumentTitle } from '../utils/useDocumentTitle';
// import '../styles/Home.css';

function Home() {
  const [categoryData, setCategoryData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [availableCategories, setAvailableCategories] = useState([]);

  // Set document title
  useDocumentTitle('RealSoft Newsletter - Home');

  // Fetch available categories
  useEffect(() => {
    const loadCategories = async () => {
      try {
        const response = await getCategories();
        if (response.success && response.data) {
          setAvailableCategories(response.data);
        } else {
          throw new Error('Failed to load categories');
        }
      } catch (err) {
        console.error('Error loading categories:', err);
        setError('Failed to load categories. Please try again later.');
      }
    };

    loadCategories();
  }, []);

  const fetchCategoryArticles = useCallback(async (pageNum = 1) => {
    if (availableCategories.length === 0) return;

    try {
      setLoading(pageNum === 1);
      if (pageNum > 1) setLoadingMore(true);

      // Create an array of promises for each category
      const categoryPromises = availableCategories.map(category =>
        getArticlesByCategory(category, pageNum)
      );

      // Execute all promises in parallel
      const results = await Promise.all(categoryPromises);

      // Process results - create a unique dataset for each category
      const newCategoryData = results.map((result, index) => {
        // Check if the result has the new format (data property in the response)
        const articlesData = result.success && result.data ? result.data : result;
        
        return {
          _id: availableCategories[index]._id,
          title: availableCategories[index].name,
          icon: getCategoryIcon(availableCategories[index].name),
          articles: articlesData || []
        };
      });


      // Ensure no duplicate categories by using _id as unique identifier
      const uniqueCategoryData = newCategoryData.filter(
        (category, index, self) => 
          index === self.findIndex(c => c._id === category._id)
      );

      // Update state based on page number
      if (pageNum === 1) {
        setCategoryData(uniqueCategoryData);
      } else {
        setCategoryData(prevData => {
          // Create a new array with merged articles
          const updated = [...prevData];
          
          uniqueCategoryData.forEach(newCategory => {
            const existingIndex = updated.findIndex(c => c._id === newCategory._id);
            if (existingIndex >= 0) {
              // Merge articles and ensure no duplicates
              const existingArticleIds = new Set(updated[existingIndex].articles.map(a => a._id));
              const newArticles = newCategory.articles.filter(a => !existingArticleIds.has(a._id));
              updated[existingIndex].articles = [...updated[existingIndex].articles, ...newArticles];
            } else {
              updated.push(newCategory);
            }
          });
          
          return updated;
        });
      }

      // Check if any category has more articles
      const hasMoreArticles = results.some(result => {
        const articlesData = result.success && result.data ? result.data : result;
        return Array.isArray(articlesData) && articlesData.length > 0;
      });
      setHasMore(hasMoreArticles);

    } catch (err) {
      console.error("Error in fetchCategoryArticles:", err);
      setError(err.message || 'Error loading articles');
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [availableCategories]);

  // Fetch articles when categories are available
  useEffect(() => {
    if (availableCategories.length > 0) {
      fetchCategoryArticles(1);
    }
  }, [availableCategories, fetchCategoryArticles]);

  const loadMore = useCallback(() => {
    if (!loadingMore && hasMore) {
      setLoadingMore(true);
      setPage(prevPage => {
        const newPage = prevPage + 1;
        fetchCategoryArticles(newPage);
        return newPage;
      });
    }
  }, [loadingMore, hasMore, fetchCategoryArticles]);

  const handleScroll = useCallback(() => {
    if (
      window.innerHeight + document.documentElement.scrollTop >=
      document.documentElement.offsetHeight - 100
    ) {
      loadMore();
    }
  }, [loadMore]);

  useEffect(() => {
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
        <p>Loading articles...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="error-container">
        <h2>Error</h2>
        <p>{error}</p>
        <button onClick={() => fetchCategoryArticles(1)}>Try Again</button>
      </div>
    );
  }

  return (
    <div>
      <Header />

      <main className="container">
        <section className="section">
          <h1 className="main-title">This Week's Highlights</h1>
          <p className="main-description">Your weekly dose of tech insights, industry trends, and expert analysis.</p>
        </section>

        {categoryData.map((category, categoryIndex) => (
          <section key={`${category._id}-${categoryIndex}`} className="section">
            <div className="section-title">
              <h2 className="category-heading">{category.title}</h2>
              <Link to={`/archive?category=${encodeURIComponent(category.title.toLowerCase())}`}>View all</Link>
            </div>

            <div className="card-grid">
              {category.articles.length > 0 ? (
                category.articles.map((article, articleIndex) => (
                  <div key={`${article._id}-${articleIndex}`} className="card">
                    <div className="card-category">
                      {category.icon}
                      {category.title}
                    </div>
                    <div className="card-content">
                      <h3 className="card-title">{article.title}</h3>
                      <p className="card-description">{article.description}</p>
                      <Link to={`/article/${article.slug}`} className="read-more-button">
                        Read More
                      </Link>
                    </div>
                  </div>
                ))
              ) : (
                <p className="no-articles">No articles found in this category.</p>
              )}
            </div>
          </section>
        ))}

        {loadingMore && (
          <div className="loading-more">
            <div className="spinner"></div>
            <p>Loading more articles...</p>
          </div>
        )}

        {!hasMore && !loadingMore && categoryData.length > 0 && (
          <div className="no-more-articles">
            <p>No more articles to load</p>
          </div>
        )}
      </main>

      <Footer />
    </div>
  );
}

export default Home; 